import { Continue } from "./screens/Continue";
import { Suspense, lazy, useEffect } from "react";
import { Route, Routes, useLocation } from "react-router";
import { ConsentBanner } from "./components/ConsentBanner";
import { ProfilePrivacyPage, WebsitePrivacyPage } from "./screens/Privacy";
import { Compare } from "./screens/Compare";
import { PrivacyChoicesPage } from "./screens/PrivacyChoices";
import { Message } from "./components/Page";
import { TERMS_URL } from "./lib/copy";
import { PageViews } from "./lib/page-views";
import { MyProfilesPage, SignInLinkPage, SignInPage } from "./screens/Account";
import { ForgotPasswordPage, ResetPasswordPage } from "./screens/PasswordReset";
import { Connect } from "./screens/Connect";
import { Go } from "./screens/Go";
import { PersonalLink } from "./screens/PersonalLink";
import { RedeemPage } from "./screens/Redeem";
import { JoinTeamPage, LeadTeamPage, TeamReportPage } from "./screens/Teams";
import { RaterFlow } from "./screens/RaterFlow";
import { Results } from "./screens/Results";
import { ResultPrint } from "./screens/ResultPrint";
import { Start } from "./screens/Start";
import { TakeFlow } from "./screens/TakeFlow";

// Staff admin: its own bundle (Better Auth, passkeys, QR), so takers never download it.
const AdminApp = lazy(() => import("./admin/AdminApp"));

const TITLES: [RegExp, string][] = [
  [/^\/take\//, "Questions · Gifts Profile"],
  [/^\/results\//, "Your results · Gifts Profile"],
  [/^\/print\/result/, "Your Gifts Profile"],
  [/^\/privacy-choices/, "Your privacy choices · Gifts Profile"],
  [/^\/privacy\/profile/, "Gifts Profile Privacy Notice"],
  [/^\/privacy/, "Website Privacy Notice · Gifts Profile"],
  [/^\/terms/, "Terms · Gifts Profile"],
  [/^\/r\//, "Describe someone · Gifts Profile"],
  [/^\/admin/, "Admin · Gifts Profile"],
  [/^\/sign-in/, "Sign in · Gifts Profile"],
  [/^\/forgot-password/, "Reset your password · Gifts Profile"],
  [/^\/reset-password/, "Choose a new password · Gifts Profile"],
  [/^\/me/, "My Gifts Profile"],
  [/^\/compare\//, "Compare your profiles · Gifts Profile"],
  [/^\/connect/, "Connect an app · Gifts Profile"],
  [/^\/t\//, "Team · Gifts Profile"],
  [/^\/teams\//, "Team report · Gifts Profile"],
];

function DocumentTitle() {
  const { pathname } = useLocation();
  useEffect(() => {
    document.title = TITLES.find(([re]) => re.test(pathname))?.[1] ?? "Gifts Profile";
  }, [pathname]);
  return null;
}

/** Sends the browser to an outside page, with a link in case it doesn't go on its own. */
function GoTo({ url, title }: { url: string; title: string }) {
  useEffect(() => {
    window.location.replace(url);
  }, [url]);
  return (
    <Message title={title}>
      <p>
        Opening <a href={url}>{url}</a>…
      </p>
    </Message>
  );
}

export function App() {
  return (
    <>
      <DocumentTitle />
      <PageViews />
      <ConsentBanner />
      <Routes>
        <Route path="/" element={<Start />} />
        <Route path="/take/:takeId" element={<TakeFlow />} />
        <Route path="/continue/:takeId" element={<Continue />} />
        <Route path="/results/:takeId" element={<Results />} />
        {/* The results PDF: the API prints this page (screens/ResultPrint). */}
        <Route path="/print/result" element={<ResultPrint />} />
        <Route path="/r/:code" element={<RaterFlow />} />
        <Route path="/go/:slug" element={<Go />} />
        <Route path="/i" element={<PersonalLink />} />
        <Route path="/redeem" element={<RedeemPage />} />
        <Route path="/sign-in" element={<SignInPage />} />
        <Route path="/sign-in/link" element={<SignInLinkPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route path="/me" element={<MyProfilesPage />} />
        <Route path="/compare/:a/:b" element={<Compare />} />
        <Route path="/connect" element={<Connect />} />
        <Route path="/t/lead" element={<LeadTeamPage />} />
        <Route path="/t/:code" element={<JoinTeamPage />} />
        <Route path="/teams/:teamId" element={<TeamReportPage />} />
        <Route
          path="/admin/*"
          element={
            <Suspense
              fallback={
                <p role="status" className="px-5 pt-16 text-muted">
                  Loading…
                </p>
              }
            >
              <AdminApp />
            </Suspense>
          }
        />
        {/* Gifts Profile's own privacy notices (Edward, Oct 26); Novos's terms (Oct 20). */}
        <Route path="/privacy-choices" element={<PrivacyChoicesPage />} />
        <Route path="/privacy" element={<WebsitePrivacyPage />} />
        <Route path="/privacy/profile" element={<ProfilePrivacyPage />} />
        <Route path="/terms" element={<GoTo url={TERMS_URL} title="Terms of Service" />} />
        <Route
          path="*"
          element={
            <Message title="Page not found">
              <p>This page doesn't exist.</p>
              <p className="mt-6">
                <a href="/">Go to the start</a>
              </p>
            </Message>
          }
        />
      </Routes>
    </>
  );
}
